import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import Select from "react-select";
import { useLocation } from "react-router-dom";
import {
  Sparkles,
  Download,
  Save,
  Loader2,
  FileText,
  Wand2,
  CheckSquare,
  Briefcase,
  MapPin,
  Building2,
  GraduationCap,
  Award,
  Eye,
  AlertCircle,
} from "lucide-react";
import "./JDGenerator.css";

import { DEEPSEEK_API_KEY } from '../../config';

const workModes = ["Onsite", "Hybrid", "Remote"];

const noticePeriods = [
  "Immediate", "0 to 15 days", "16 to 30 days",
  "31 to 45 days", "45 to 60 days", "More than 60 days"
];

const hiringTypes = [
  { label: "Internship", value: "Internship" },
  { label: "Fulltime", value: "Fulltime" },
  { label: "Casual (Fixed time period)", value: "Casual" },
];

const fixedTimePeriods = [
  { label: "3 Months", value: "3" }, { label: "6 Months", value: "6" },
  { label: "9 Months", value: "9" }, { label: "12 Months", value: "12" },
  { label: "15 Months", value: "15" }, { label: "18 Months", value: "18" },
  { label: "24 Months", value: "24" }, { label: "28 Months", value: "28" },
  { label: "32 Months", value: "32" }, { label: "36 Months", value: "36" },
];

const benefitsList = [
  "Visa provided", "Medical insurances", "Joining Tickets",
  "Return Tickets", "Service charges"
];

const JDGenerator = () => {
  const query = new URLSearchParams(useLocation().search);
  const editId = query.get("id");
  const isEdit = useMemo(() => !!editId, [editId]);

  const [formData, setFormData] = useState({
    job_role: "",
    experience: "",
    skills: "",
    location: "",
    work_mode: "",
    work_location: "",
    shift: "",
    budget: "",
    hiring_type: "",
    fixed_time_period: "",
    notice_period: "",
    about_company: "",
    qualification: "",
    good_to_have: "",
    roles_responsibilities: "",
    benefits: [],
  });

  const [generatedJD, setGeneratedJD] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [apiWaitMessage, setApiWaitMessage] = useState("");

  const [dynamicQualifications, setDynamicQualifications] = useState([
    "Bachelor's in Hotel Management", "Diploma in Culinary Arts",
    "Any Graduate", "MBA / BBA", "High School Diploma"
  ]);

  const [dynamicSkills, setDynamicSkills] = useState([
    "Guest Relations", "F&B Operations", "Front Office Management",
    "Housekeeping", "Revenue Management", "CRM Software", "Multilingual"
  ]);

  useEffect(() => {
    const fetchOne = async () => {
      if (!isEdit) return;
      setLoadingPrefill(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-description/${editId}`);
        const jd = res.data;
        setFormData({
          job_role: jd.job_role || "",
          experience: jd.experience || "",
          skills: Array.isArray(jd.skills) ? jd.skills.join(", ") : jd.skills || "",
          location: jd.location || "",
          work_mode: jd.work_mode || "",
          work_location: jd.work_location || "",
          shift: jd.shift || "",
          budget: jd.budget || "",
          hiring_type: jd.hiring_type || "",
          fixed_time_period: jd.fixed_time_period || "",
          notice_period: jd.notice_period || "",
          about_company: jd.about_company || "",
          qualification: jd.qualification || "",
          good_to_have: jd.good_to_have || "",
          roles_responsibilities: jd.roles_responsibilities || "",
          benefits: jd.benefits || [],
        });
        setGeneratedJD(jd.generated_jd || "");
      } catch (e) {
        console.error("Failed to prefill JD:", e?.response?.data || e.message);
        alert("Failed to load JD for editing.");
      } finally {
        setLoadingPrefill(false);
      }
    };
    fetchOne();
  }, [isEdit, editId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, s) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: s ? s.value : "" };
      if (name === "hiring_type" && s?.value !== "Casual") {
        newData.fixed_time_period = "";
      }
      return newData;
    });
  };

  const handleCheckboxToggle = (field, value, isChecked) => {
    setFormData((prev) => {
      if (field === "benefits") {
        const currentBenefits = [...prev.benefits];
        if (isChecked) currentBenefits.push(value);
        else return { ...prev, benefits: currentBenefits.filter(b => b !== value) };
        return { ...prev, benefits: currentBenefits };
      } else {
        let currentArr = prev[field] ? prev[field].split(',').map(s => s.trim()).filter(Boolean) : [];
        if (isChecked && !currentArr.includes(value)) currentArr.push(value);
        else currentArr = currentArr.filter(item => item !== value);
        return { ...prev, [field]: currentArr.join(', ') };
      }
    });
  };

  // --- Helper for API calls with built-in retry for 429 errors ---
  const callDeepSeekWithRetry = async (prompt, retries = 2, delay = 2000) => {
    const apiKey = DEEPSEEK_API_KEY;
    if (!apiKey) throw new Error("API Key missing.");

    for (let i = 0; i < retries; i++) {
      try {
        const response = await axios.post(
          `https://api.deepseek.com/chat/completions`,
          {
            model: "deepseek-chat",
            messages: [{ role: "user", content: prompt }],
            stream: false
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`
            }
          }
        );
        setApiWaitMessage("");
        return response;
      } catch (error) {
        if (error.response && error.response.status === 429 && i < retries - 1) {
          console.warn(`Rate limit hit (429). Retrying in ${delay / 1000} seconds...`);
          setApiWaitMessage(`API busy. Retrying in ${delay / 1000}s...`);
          await new Promise(res => setTimeout(res, delay));
          delay *= 2;
        } else {
          setApiWaitMessage("");
          throw error;
        }
      }
    }
  };

  const handleAutoFill = async () => {
    if (!formData.job_role) return alert("Please enter a Job Title first.");

    setAutofilling(true);
    const prompt = `
      Act as an expert Hospitality Recruiter. 
      I need typical requirements for the job role: "${formData.job_role}".
      Return ONLY a valid JSON object. No markdown, no backticks.
      {
        "experience": "e.g. 3-5 years in luxury hotels",
        "skills": "comma separated list of top 5 mandatory skills",
        "qualification": "typical degree/diploma required",
        "good_to_have": "comma separated list of bonus skills",
        "roles_responsibilities": "A bulleted list of 5-7 key daily duties",
        "about_company": "A generic but professional placeholder about a premium hospitality brand",
        "suggested_qualifications_list": ["Suggest 4 to 5 relevant degree/diploma options as an array of strings"],
        "suggested_skills_list": ["Suggest 6 to 8 relevant specific skills as an array of strings"]
      }
    `;

    try {
      const response = await callDeepSeekWithRetry(prompt);
      let textResponse = response.data?.choices?.[0]?.message?.content;

      if (textResponse) {
        textResponse = textResponse.replace(/```json/g, '').replace(/```/g, '').trim();
        const aiData = JSON.parse(textResponse);

        setFormData(prev => ({
          ...prev,
          experience: aiData.experience || prev.experience,
          skills: aiData.skills || prev.skills,
          qualification: aiData.qualification || prev.qualification,
          good_to_have: aiData.good_to_have || prev.good_to_have,
          roles_responsibilities: aiData.roles_responsibilities || prev.roles_responsibilities,
          about_company: prev.about_company || aiData.about_company
        }));

        if (aiData.suggested_qualifications_list && Array.isArray(aiData.suggested_qualifications_list)) {
          setDynamicQualifications(aiData.suggested_qualifications_list);
        }
        if (aiData.suggested_skills_list && Array.isArray(aiData.suggested_skills_list)) {
          setDynamicSkills(aiData.suggested_skills_list);
        }
      }
    } catch (error) {
      console.error("Auto-fill Error:", error);
      if (error.response?.status === 429) {
        alert("DeepSeek AI is currently busy (Too Many Requests). Please try again in a minute.");
      } else {
        alert("Could not auto-fetch details. Please fill manually.");
      }
    } finally {
      setAutofilling(false);
      setApiWaitMessage("");
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!formData.job_role) return alert("Please enter a Job Title.");

    setGenerating(true);

    // ── Build clean readable strings for each field ──
    const skillsList = formData.skills
      ? formData.skills.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    const qualificationsList = formData.qualification
      ? formData.qualification.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    const goodToHaveList = formData.good_to_have
      ? formData.good_to_have.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    const benefitsProvided = formData.benefits && formData.benefits.length > 0
      ? formData.benefits
      : [];

    const prompt = `
You are an expert HR Recruiter specializing in the Hospitality and Service industry.
Write a warm, professional, and detailed Job Description for the role of "${formData.job_role}".

Use EXACTLY the information provided below. Do NOT make up or substitute any details.

=== JOB DETAILS ===
- Job Title: ${formData.job_role}
- Employment Type: ${formData.hiring_type || "Full-time"}${formData.fixed_time_period ? ` (${formData.fixed_time_period} months contract)` : ""}
- Work Location: ${formData.location || "Not specified"}
- Work Mode: ${formData.work_mode || "Not specified"}
- Shift: ${formData.shift || "Standard shift"}
- Experience Required: ${formData.experience || "Not specified"}
- Salary / CTC: ${formData.budget || "As per industry standards"}
- Notice Period: ${formData.notice_period || "Not specified"}

=== ABOUT THE COMPANY ===
${formData.about_company || "A leading hospitality organization committed to excellence and guest satisfaction."}

=== ROLES AND RESPONSIBILITIES ===
${formData.roles_responsibilities || "Standard hospitality management responsibilities."}

=== MANDATORY QUALIFICATIONS ===
${qualificationsList.length > 0
  ? qualificationsList.map(q => `- ${q}`).join('\n')
  : "- As per industry standard"}

=== MANDATORY SKILLS (MUST be listed exactly as provided) ===
${skillsList.length > 0
  ? skillsList.map(s => `- ${s}`).join('\n')
  : "- As per industry standard"}

=== GOOD TO HAVE SKILLS ===
${goodToHaveList.length > 0
  ? goodToHaveList.map(s => `- ${s}`).join('\n')
  : "- Not specified"}

=== EMPLOYEE BENEFITS (MUST list every benefit exactly as provided) ===
${benefitsProvided.length > 0
  ? benefitsProvided.map(b => `- ${b}`).join('\n')
  : "- Standard company benefits"}

=== FORMATTING RULES (STRICTLY FOLLOW) ===
1. Do NOT use any markdown - no asterisks (**), no hashes (#), no bullet symbol (*).
2. Use ALL CAPS for every section heading.
3. Use a dash (-) for all list items.
4. Add one blank line between sections.
5. You MUST include these sections in this order:
   ABOUT THE COMPANY
   ABOUT THE ROLE
   ROLES AND RESPONSIBILITIES
   MANDATORY QUALIFICATIONS
   SKILLS REQUIRED
   GOOD TO HAVE SKILLS
   COMPENSATION AND BENEFITS
   EMPLOYEE BENEFITS
   NOTICE PERIOD
6. Under SKILLS REQUIRED, list EVERY skill from the mandatory skills list above exactly.
7. Under MANDATORY QUALIFICATIONS, list EVERY qualification from the list above exactly.
8. Under EMPLOYEE BENEFITS, list EVERY benefit from the benefits list above exactly.
9. Do NOT skip or summarize any section. Write each section in full.
    `;

    try {
      const response = await callDeepSeekWithRetry(prompt);
      let aiText = response.data?.choices?.[0]?.message?.content;

      if (aiText) {
        aiText = aiText.replace(/\*\*/g, '').replace(/#/g, '').replace(/^\* /gm, '- ');
        setGeneratedJD(aiText.trim());
      }
    } catch (error) {
      console.error("Generate Error:", error);
      if (error.response?.status === 429) {
        alert("DeepSeek AI is currently busy (Too Many Requests). Please try again in a minute.");
      } else {
        alert("Failed to generate JD using AI. Check console.");
      }
    } finally {
      setGenerating(false);
      setApiWaitMessage("");
    }
  };

  // --- PROFESSIONAL PDF GENERATOR ---
  const renderPdf = () => {
    const doc = new jsPDF();
    const margin = 20;
    let y = 35;

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`Job Description: ${formData.job_role || 'Position Details'}`, margin, 14);

    doc.setTextColor(30, 41, 59);
    const textToRender = generatedJD || "No content available.";
    const lines = textToRender.split('\n');
    const lineHeight = 6;
    const pageHeight = doc.internal.pageSize.height;

    lines.forEach(line => {
      if (y + lineHeight > pageHeight - margin) {
        doc.addPage();
        y = margin + 10;
      }

      const trimmedLine = line.trim();
      const isHeading = trimmedLine.length > 3 && trimmedLine === trimmedLine.toUpperCase();

      if (isHeading) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        y += 3;
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
      }

      const splitLines = doc.splitTextToSize(line, doc.internal.pageSize.width - 2 * margin);

      splitLines.forEach(splitLine => {
        if (y + lineHeight > pageHeight - margin) {
          doc.addPage();
          y = margin + 10;
          doc.setFont("helvetica", "normal");
        }
        doc.text(splitLine, margin, y);
        y += lineHeight;
      });
    });

    return doc;
  };

  const handleDownload = () => {
    if (!generatedJD) return alert("Assemble preview first.");
    const doc = renderPdf();
    doc.save("Job_Description.pdf");
  };

  const saveToServer = async (method, url, jdName) => {
    const doc = renderPdf();
    const pdfBlob = doc.output("blob");
    const fileName = `${(formData.job_role || "JD").replace(/[^\w.-]+/g, "_")}_${Date.now()}.pdf`;

    const fd = new FormData();
    fd.append("jdPdf", pdfBlob, fileName);

    Object.entries(formData).forEach(([k, v]) => {
      if (k === 'benefits') {
        fd.append(k, JSON.stringify(v));
      } else {
        fd.append(k, v ?? "");
      }
    });

    fd.append("generated_jd", generatedJD);
    fd.append("jd_name", jdName);

    const recruiterId = localStorage.getItem('recruiterId');
    const customerId = localStorage.getItem('customerId');

    if (recruiterId) {
      fd.append('creatorId', recruiterId);
      fd.append('creatorRole', 'Recruiter');
    } else if (customerId) {
      fd.append('creatorId', customerId);
      fd.append('creatorRole', 'Customer');
    }

    const config = { headers: { "Content-Type": "multipart/form-data" } };
    if (method === "post") return axios.post(url, fd, config);
    return axios.put(url, fd, config);
  };

  const handleSave = async () => {
    if (!generatedJD) return alert("Assemble preview first.");
    const jdName = window.prompt("Enter a name for this JD:");
    if (!jdName) return;

    setSaving(true);
    try {
      const url = isEdit
        ? `${process.env.REACT_APP_API_BASE_URL}/api/job-description/${editId}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/job-description/save`;
      const method = isEdit ? "put" : "post";
      await saveToServer(method, url, jdName.trim());
      alert(`JD ${isEdit ? 'updated' : 'saved'} successfully!`);
    } catch (err) {
      alert("Failed to save JD");
    } finally {
      setSaving(false);
    }
  };

  const isStringChecked = (fieldString, value) => {
    if (!fieldString) return false;
    const arr = fieldString.split(',').map(s => s.trim());
    return arr.includes(value);
  };

  return (
    <div className="jg-root">
      <div className="jg-bg-blob jg-blob-1" />
      <div className="jg-bg-blob jg-blob-2" />

      {/* ── Header ── */}
      <div className="jg-header">
        <div className="jg-header-left">
          <div className="jg-title-icon">
            <Wand2 size={24} />
          </div>
          <div>
            <h1 className="jg-title">{isEdit ? "Edit Job Description" : "JD Generator"}</h1>
            <p className="jg-subtitle">AI-powered hospitality job descriptions in seconds</p>
          </div>
        </div>
      </div>

      {loadingPrefill ? (
        <div className="jg-loading">
          <div className="jg-loading-card">
            <Loader2 className="jg-spin" size={36} />
            <span>Loading JD data…</span>
          </div>
        </div>
      ) : (
        <div className="jg-container">

          {/* ═══ LEFT: FORM ═══ */}
          <div className="jg-form-col">
            <form onSubmit={handleGenerate} className="jg-form">

              {/* AI Autofill Card */}
              <div className="jg-card jg-ai-card">
                <div className="jg-ai-header">
                  <div className="jg-ai-icon">
                    <Sparkles size={18} />
                  </div>
                  <div>
                    <h3>Start with AI Autofill</h3>
                    <p>Type a job title and let AI pre-fill the form</p>
                  </div>
                </div>

                <div className="jg-field jg-full">
                  <label htmlFor="job_role">
                    Job Title <span className="jg-required">*</span>
                  </label>
                  <div className="jg-ai-input-row">
                    <input
                      id="job_role"
                      name="job_role"
                      value={formData.job_role}
                      onChange={handleChange}
                      required
                      className="jg-input"
                      placeholder="e.g. Hotel General Manager"
                    />
                    <button
                      type="button"
                      onClick={handleAutoFill}
                      disabled={autofilling}
                      className="jg-ai-btn"
                    >
                      {autofilling ? (
                        <><Loader2 size={15} className="jg-spin" /> Filling…</>
                      ) : (
                        <><Sparkles size={15} /> Auto-Fill</>
                      )}
                    </button>
                  </div>
                  {apiWaitMessage && (
                    <span className="jg-api-wait">
                      <AlertCircle size={12} /> {apiWaitMessage}
                    </span>
                  )}
                </div>
              </div>

              {/* Section: Job Basics */}
              <div className="jg-card">
                <div className="jg-section-header">
                  <div className="jg-section-icon">
                    <Briefcase size={16} />
                  </div>
                  <h3>Job Basics</h3>
                </div>

                <div className="jg-grid">
                  <div className="jg-field">
                    <label htmlFor="hiring_type">Type of Hiring</label>
                    <Select
                      id="hiring_type"
                      classNamePrefix="react-select"
                      options={hiringTypes}
                      value={hiringTypes.find(opt => opt.value === formData.hiring_type) || null}
                      onChange={(s) => handleSelectChange("hiring_type", s)}
                      required
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  {formData.hiring_type === "Casual" && (
                    <div className="jg-field jg-fade-in">
                      <label htmlFor="fixed_time_period">Fixed Time Period (Months) *</label>
                      <Select
                        id="fixed_time_period"
                        classNamePrefix="react-select"
                        options={fixedTimePeriods}
                        value={fixedTimePeriods.find(opt => opt.value === formData.fixed_time_period) || null}
                        onChange={(s) => handleSelectChange("fixed_time_period", s)}
                        required
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  )}

                  <div className="jg-field">
                    <label htmlFor="location">Work Location</label>
                    <input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="jg-input"
                      placeholder="e.g. Mumbai, Dubai"
                    />
                  </div>

                  <div className="jg-field">
                    <label htmlFor="work_mode">Work Mode</label>
                    <Select
                      id="work_mode"
                      classNamePrefix="react-select"
                      options={workModes.map((m) => ({ label: m, value: m }))}
                      value={formData.work_mode ? { label: formData.work_mode, value: formData.work_mode } : null}
                      onChange={(s) => handleSelectChange("work_mode", s)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="jg-field">
                    <label htmlFor="budget">Salary / CTC</label>
                    <input
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="jg-input"
                      placeholder="e.g. 15 LPA or $50,000"
                    />
                  </div>

                  <div className="jg-field">
                    <label htmlFor="notice_period">Notice Period</label>
                    <Select
                      id="notice_period"
                      classNamePrefix="react-select"
                      options={noticePeriods.map((p) => ({ label: p, value: p }))}
                      value={formData.notice_period ? { label: formData.notice_period, value: formData.notice_period } : null}
                      onChange={(s) => handleSelectChange("notice_period", s)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="jg-field">
                    <label htmlFor="experience">Experience Required</label>
                    <input
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="jg-input"
                      placeholder="e.g. 3-5 Years"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Company & Role */}
              <div className="jg-card">
                <div className="jg-section-header">
                  <div className="jg-section-icon jg-icon-purple">
                    <Building2 size={16} />
                  </div>
                  <h3>Company & Role Details</h3>
                </div>

                <div className="jg-field jg-full">
                  <label htmlFor="about_company">About Company</label>
                  <textarea
                    id="about_company"
                    name="about_company"
                    value={formData.about_company}
                    onChange={handleChange}
                    rows={3}
                    className="jg-textarea"
                    placeholder="Briefly describe the hotel or establishment…"
                  />
                </div>

                <div className="jg-field jg-full">
                  <label htmlFor="roles_responsibilities">Roles and Responsibilities</label>
                  <textarea
                    id="roles_responsibilities"
                    name="roles_responsibilities"
                    value={formData.roles_responsibilities}
                    onChange={handleChange}
                    rows={4}
                    className="jg-textarea"
                    placeholder="Key duties…"
                  />
                </div>
              </div>

              {/* Section: Qualification */}
              <div className="jg-card">
                <div className="jg-section-header">
                  <div className="jg-section-icon jg-icon-amber">
                    <GraduationCap size={16} />
                  </div>
                  <h3>Qualification(s)</h3>
                </div>

                <div className="jg-field jg-full">
                  <input
                    id="qualification"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    className="jg-input"
                    placeholder="Type custom qualifications or pick from below…"
                  />
                  <div className="jg-checkbox-grid">
                    {dynamicQualifications.map(q => (
                      <label key={q} className={`jg-smart-checkbox ${isStringChecked(formData.qualification, q) ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isStringChecked(formData.qualification, q)}
                          onChange={(e) => handleCheckboxToggle("qualification", q, e.target.checked)}
                        />
                        <span>{q}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Section: Skills */}
              <div className="jg-card">
                <div className="jg-section-header">
                  <div className="jg-section-icon jg-icon-rose">
                    <Award size={16} />
                  </div>
                  <h3>Skills Required</h3>
                </div>

                <div className="jg-field jg-full">
                  <label htmlFor="skills">Mandatory Skills</label>
                  <input
                    id="skills"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    className="jg-input"
                    placeholder="Type skills or pick from below…"
                  />
                  <div className="jg-checkbox-grid">
                    {dynamicSkills.map(s => (
                      <label key={s} className={`jg-smart-checkbox ${isStringChecked(formData.skills, s) ? 'checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isStringChecked(formData.skills, s)}
                          onChange={(e) => handleCheckboxToggle("skills", s, e.target.checked)}
                        />
                        <span>{s}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="jg-field jg-full" style={{ marginTop: 14 }}>
                  <label htmlFor="good_to_have">Good to Have Skills</label>
                  <input
                    id="good_to_have"
                    name="good_to_have"
                    value={formData.good_to_have}
                    onChange={handleChange}
                    className="jg-input"
                    placeholder="e.g. Leadership, MS Excel"
                  />
                </div>
              </div>

              {/* Section: Benefits */}
              <div className="jg-card">
                <div className="jg-section-header">
                  <div className="jg-section-icon jg-icon-green">
                    <CheckSquare size={16} />
                  </div>
                  <h3>Employee Benefits</h3>
                </div>

                <div className="jg-checkbox-grid">
                  {benefitsList.map(b => (
                    <label key={b} className={`jg-smart-checkbox jg-benefit ${formData.benefits.includes(b) ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={formData.benefits.includes(b)}
                        onChange={(e) => handleCheckboxToggle("benefits", b, e.target.checked)}
                      />
                      <span>{b}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="jg-submit-wrap">
                <button type="submit" className="jg-submit-btn" disabled={generating || autofilling}>
                  {generating ? (
                    <><Loader2 size={16} className="jg-spin" /> Generating JD…</>
                  ) : (
                    <><Wand2 size={16} /> Assemble Preview with AI</>
                  )}
                </button>
                {apiWaitMessage && !autofilling && (
                  <span className="jg-api-wait jg-api-wait-center">
                    <AlertCircle size={12} /> {apiWaitMessage}
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* ═══ RIGHT: PREVIEW ═══ */}
          <div className="jg-preview-col">
            <div className="jg-preview-sticky">
              <div className="jg-preview-header">
                <div className="jg-preview-title">
                  <Eye size={16} />
                  <h4>JD Preview</h4>
                </div>
                <span className="jg-preview-tag">
                  <FileText size={11} /> Live Editor
                </span>
              </div>

              <textarea
                className="jg-preview-textarea"
                value={generatedJD}
                onChange={(e) => setGeneratedJD(e.target.value)}
                placeholder="Your AI-generated job description will appear here. You can edit it manually before saving."
              />

              {generatedJD && (
                <div className="jg-preview-actions">
                  <button className="jg-download-btn" onClick={handleDownload}>
                    <Download size={15} /> Download PDF
                  </button>
                  <button className="jg-save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <><Loader2 size={15} className="jg-spin" /> Saving…</>
                    ) : (
                      <><Save size={15} /> {isEdit ? "Update JD" : "Save JD"}</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default JDGenerator;